import { Box, Button, IconButton, Tooltip, Typography } from '@mui/material'
import { useRef, useState } from 'react'
import { useReactToPrint } from 'react-to-print'
import CircularProgress from '@mui/material/CircularProgress'
import { COLORS } from '~theme/colors'
import { PrinterIcon } from '~components/icons'
import { useGetPortInfoById } from '~hooks/queries/port-info/use-get-port-info'
import { ExactScopeName, useHasScope } from '~hooks/permissions/use-has-scope'
import { useDateFormat } from '~hooks/use-date-format'
import { formatDate, formatDateOnly } from '~utils/dates'
import { EditVisitModal } from './edit-visit/edit-visit-modal'
import type { PortInfoItem } from '../types'
import { REMARK_FIELDS } from '../types'
import { sectionBoxSx, fieldsContainerSx, remarksGridSx } from '../styles'
import { getFilesForField } from '../utils/form-utils'
import { InfoBlock, RemarkBlock } from './shared'

const formatUnit = (value: number | null | undefined, unit: string): string =>
  value != null ? `${value} ${unit}` : '-'

const formatDraftUnit = (
  value: number | null | undefined,
  unit: string,
): string => (value != null ? `${value.toFixed(2)} ${unit}` : '-')

interface BerthDetailProps {
  visitId: string
}

export function BerthDetail({ visitId }: BerthDetailProps) {
  const { data: visit, isLoading } = useGetPortInfoById(visitId)
  const [showEditModal, setShowEditModal] = useState(false)
  const dateFormat = useDateFormat('literal')
  const updateDateFormat = useDateFormat('with hours and minutes')
  const canWritePortInfo = useHasScope(
    ExactScopeName.technicalManagement,
    ExactScopeName.portInfoWrite,
  )
  // biome-ignore lint/suspicious/noExplicitAny: any is needed for ref typing in react-to-print
  const printRef = useRef<HTMLDivElement>(null as any)
  const handlePrint = useReactToPrint({ contentRef: printRef })

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress aria-label="Loading…" />
      </Box>
    )
  }

  if (!visit) {
    return (
      <Typography variant="paragraph2" color={COLORS.greys.dark}>
        Unable to load berth details
      </Typography>
    )
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
        <Tooltip title="Print Visit">
          <IconButton size="small" onClick={() => handlePrint()}>
            <PrinterIcon width="20px" height="20px" />
          </IconButton>
        </Tooltip>
        {canWritePortInfo && (
          <Button
            variant="outlined"
            size="small"
            onClick={() => setShowEditModal(true)}
          >
            Edit Visit
          </Button>
        )}
      </Box>
      <Box ref={printRef}>
        <Box sx={sectionBoxSx}>
          <Typography variant="paragraphBold2">Visit Information</Typography>
          <Box sx={fieldsContainerSx}>
            <InfoBlock
              label="Port"
              value={`${visit.portName} (${visit.portCode})`}
            />
            <InfoBlock label="GISIS Code" value={visit.gisisCode || '-'} />
            <InfoBlock label="Terminal" value={visit.terminalName} />
            <InfoBlock label="Berth" value={visit.berthName} />
            <InfoBlock
              label="Vessel"
              value={`${visit.vesselName} (${visit.imoNumber})`}
            />
            <InfoBlock label="Pool/Segment" value={visit.pool || '-'} />
          </Box>
        </Box>

        <Box sx={sectionBoxSx}>
          <Typography variant="paragraphBold2">Voyage Details</Typography>
          <Box sx={fieldsContainerSx}>
            <InfoBlock
              label="Date of Arrival"
              value={
                visit.dateOfArrival
                  ? formatDateOnly(visit.dateOfArrival, dateFormat)
                  : '-'
              }
            />
            <InfoBlock
              label="Date of Departure"
              value={formatDateOnly(visit.dateOfDeparture, dateFormat)}
            />
            <InfoBlock label="Operation" value={visit.operation || '-'} />
            <InfoBlock label="Cargo Type" value={visit.cargoType} />
          </Box>
        </Box>

        <Box sx={sectionBoxSx}>
          <Typography variant="paragraphBold2">
            Draft Information Related to this Visit
          </Typography>
          <Box sx={fieldsContainerSx}>
            <InfoBlock
              label="Arrival Draft"
              value={formatDraftUnit(visit.arrivalDraft, 'm')}
            />
            <InfoBlock
              label="Departure Draft"
              value={formatDraftUnit(visit.departureDraft, 'm')}
            />
            <InfoBlock
              label="Air Draft"
              value={formatDraftUnit(visit.airDraft, 'm')}
            />
          </Box>
        </Box>

        <Box sx={sectionBoxSx}>
          <Typography variant="paragraphBold2">Cargo Operations</Typography>
          <Box sx={fieldsContainerSx}>
            <InfoBlock
              label="ISPS Security Level"
              value={visit.ispsSecurityLevel}
            />
            <InfoBlock
              label="Cargo Connections"
              value={visit.cargoConnections}
            />
            <InfoBlock
              label="Manifold Pressure"
              value={formatUnit(visit.manifoldPressure, 'bar')}
            />
            <InfoBlock
              label="Load/Discharge Rate"
              value={formatUnit(visit.loadDischargeRate, 'm³/hr')}
            />
          </Box>
        </Box>

        <Box sx={sectionBoxSx}>
          <Typography variant="paragraphBold2">Port Services</Typography>
          <Box sx={fieldsContainerSx}>
            <InfoBlock
              label="Fresh Water Available"
              value={visit.freshWaterAvailable}
            />
            <InfoBlock
              label="Garbage Landing Available"
              value={visit.garbageLandingAvailable}
            />
            <InfoBlock
              label="Slops/Sludge Landing Available"
              value={visit.slopsSludgeLandingAvailable}
            />
            <InfoBlock
              label="De-Slopping Available"
              value={visit.deSloppingAvailable}
            />
            <InfoBlock
              label="Dock Water Density"
              value={
                visit.dockWaterDensity != null
                  ? String(visit.dockWaterDensity)
                  : '-'
              }
            />
          </Box>
        </Box>

        <Box sx={sectionBoxSx}>
          <Typography variant="paragraphBold2">General Remarks</Typography>
          <Box sx={remarksGridSx}>
            {REMARK_FIELDS.map(({ label, key }) => {
              const files = getFilesForField(visit.files, key)
              return (
                <RemarkBlock
                  key={key}
                  label={label}
                  value={visit[key as keyof PortInfoItem] as string | null}
                  attachedFiles={files}
                  variant="compact"
                />
              )
            })}
          </Box>
        </Box>

        <Typography
          variant="caption"
          sx={{ color: COLORS.greys.dark }}
          component="p"
        >
          Last updated by {visit?.updatedBy} on{' '}
          {formatDate(visit?.updatedAt || '', updateDateFormat)}
        </Typography>
      </Box>

      {showEditModal && (
        <EditVisitModal
          visitId={visitId}
          onClose={() => setShowEditModal(false)}
        />
      )}
    </Box>
  )
}
